export const STANDARD_TTS_MODEL_ID = 'standard'
export const ODUB_MODEL_ID = 'odub'
export const ODUB_MULTILINGUAL_MODEL_ID = 'multilingual'
export const ODUB_FINETUNED_MODEL_ID = 'finetuned'
export const ODUB_DESIGN_VOICE_CLONE_MODEL_ID = 'prompted'

export const STANDARD_TTS_MODEL_LABEL = 'Default model'
export const ODUB_MODEL_LABEL = 'odub™'
export const ODUB_MULTILINGUAL_MODEL_LABEL = 'odub™ Multilingual'
export const ODUB_FINETUNED_MODEL_LABEL = 'odub™ Finetuned'
export const ODUB_DESIGN_VOICE_CLONE_MODEL_LABEL = 'odub™ Design'

// We expose a single odub option in the UI and resolve it internally to the
// concrete odub backend model supported for the requested language(s).
const SHOW_ODUB_TTS_MODEL_OPTIONS = true
export const DEFAULT_ODUB_VOICE_CLONING_MODEL_ID = ODUB_MODEL_ID
export const ODUB_CHATTERBOX_VOICE_CLONING_SUPPORTED_LANGUAGE_IDS = [
  'ar',
  'da',
  'de',
  'el',
  'en',
  'es',
  'fi',
  'fr',
  'he',
  'hi',
  'it',
  'ja',
  'ko',
  'ms',
  'nl',
  'no',
  'pl',
  'pt',
  'ru',
  'sv',
  'sw',
  'tr',
  'zh',
] as const
// Based on odub's currently documented high-quality supported languages.
export const ODUB_FINETUNED_VOICE_CLONING_SUPPORTED_LANGUAGE_IDS = [
  'de',
  'en',
  'es',
  'fr',
] as const

export const VOICE_CLONING_ENGINE_OPTIONS = [
  {
    id: STANDARD_TTS_MODEL_ID,
    label: STANDARD_TTS_MODEL_LABEL,
    buttonLabel: STANDARD_TTS_MODEL_LABEL,
    description: 'Uses the current default TTS provider.',
  },
  {
    id: ODUB_MODEL_ID,
    label: ODUB_MODEL_LABEL,
    buttonLabel: ODUB_MODEL_LABEL,
    description: 'Uses odub as TTS provider.',
  },
] as const

const ODUB_LANGUAGE_NAME_PREFIX_TO_CODE: { [key: string]: string } = {
  arabic: 'ar',
  chinese: 'zh',
  danish: 'da',
  dutch: 'nl',
  english: 'en',
  finnish: 'fi',
  french: 'fr',
  german: 'de',
  greek: 'el',
  hebrew: 'he',
  hindi: 'hi',
  italian: 'it',
  japanese: 'ja',
  korean: 'ko',
  malay: 'ms',
  norwegian: 'no',
  polish: 'pl',
  portuguese: 'pt',
  russian: 'ru',
  spanish: 'es',
  swahili: 'sw',
  swedish: 'sv',
  turkish: 'tr',
}

export const normalizeOdubVoiceCloningLanguageCode = (languageCode: string | null | undefined) => {
  if (!languageCode) { return undefined }

  const normalizedLanguageCode = languageCode.toLowerCase().trim()

  if (normalizedLanguageCode === 'cmn') {
    return 'zh'
  }

  const mappedLanguageCode = Object.entries(ODUB_LANGUAGE_NAME_PREFIX_TO_CODE).find(([languageNamePrefix]) => normalizedLanguageCode.startsWith(languageNamePrefix))?.[1]

  if (mappedLanguageCode) {
    return mappedLanguageCode
  }

  return normalizedLanguageCode.split(/[-_]/)[0]
}

export const supportsOdubChatterboxVoiceCloningLanguage = (languageCode: string | null | undefined) => {
  const normalizedLanguageCode = normalizeOdubVoiceCloningLanguageCode(languageCode)

  return !!normalizedLanguageCode && ODUB_CHATTERBOX_VOICE_CLONING_SUPPORTED_LANGUAGE_IDS.includes(normalizedLanguageCode as typeof ODUB_CHATTERBOX_VOICE_CLONING_SUPPORTED_LANGUAGE_IDS[number])
}

export const supportsOdubFinetunedVoiceCloningLanguage = (languageCode: string | null | undefined) => {
  const normalizedLanguageCode = normalizeOdubVoiceCloningLanguageCode(languageCode)

  return !!normalizedLanguageCode && ODUB_FINETUNED_VOICE_CLONING_SUPPORTED_LANGUAGE_IDS.includes(normalizedLanguageCode as typeof ODUB_FINETUNED_VOICE_CLONING_SUPPORTED_LANGUAGE_IDS[number])
}

export const getAutomaticOdubVoiceCloningModelId = (languageCode: string | null | undefined) => {
  if (supportsOdubFinetunedVoiceCloningLanguage(languageCode)) { return ODUB_FINETUNED_MODEL_ID }
  if (supportsOdubChatterboxVoiceCloningLanguage(languageCode)) { return ODUB_MULTILINGUAL_MODEL_ID }

  return undefined
}

export const getAutomaticOdubVoiceCloningModelIdForLanguages = (languageCodes: (string | null | undefined)[]) => {
  const relevantLanguageCodes = languageCodes.filter((languageCode): languageCode is string => !!languageCode)

  if (relevantLanguageCodes.length === 0) {
    return ODUB_FINETUNED_MODEL_ID
  }

  if (relevantLanguageCodes.every(languageCode => supportsOdubFinetunedVoiceCloningLanguage(languageCode))) {
    return ODUB_FINETUNED_MODEL_ID
  }

  if (relevantLanguageCodes.every(languageCode => supportsOdubChatterboxVoiceCloningLanguage(languageCode))) {
    return ODUB_MULTILINGUAL_MODEL_ID
  }

  return undefined
}

export const getResolvedVoiceCloningModelId = (engineId: string | null | undefined, languageCode: string | null | undefined) => {
  if (!SHOW_ODUB_TTS_MODEL_OPTIONS) { return undefined }
  if (engineId !== ODUB_MODEL_ID) { return undefined }

  return getAutomaticOdubVoiceCloningModelId(languageCode)
}

export const getResolvedVoiceCloningModelIdForLanguages = (engineId: string | null | undefined, languageCodes: (string | null | undefined)[]) => {
  if (!SHOW_ODUB_TTS_MODEL_OPTIONS) { return undefined }
  if (engineId !== ODUB_MODEL_ID) { return undefined }

  return getAutomaticOdubVoiceCloningModelIdForLanguages(languageCodes)
}

export const getDefaultVoiceCloningEngineId = (languageCode: string | null | undefined) => {
  if (!SHOW_ODUB_TTS_MODEL_OPTIONS) { return STANDARD_TTS_MODEL_ID }
  if (getAutomaticOdubVoiceCloningModelId(languageCode)) { return DEFAULT_ODUB_VOICE_CLONING_MODEL_ID }

  return STANDARD_TTS_MODEL_ID
}

export const getVoiceCloningEngineOptions = (languageCode: string | null | undefined) => {
  if (!SHOW_ODUB_TTS_MODEL_OPTIONS) {
    return VOICE_CLONING_ENGINE_OPTIONS.filter(option => option.id === STANDARD_TTS_MODEL_ID)
  }

  if (!languageCode) {
    return VOICE_CLONING_ENGINE_OPTIONS
  }

  const automaticOdubVoiceCloningModelId = getAutomaticOdubVoiceCloningModelId(languageCode)

  if (!automaticOdubVoiceCloningModelId) {
    return VOICE_CLONING_ENGINE_OPTIONS.filter(option => option.id === STANDARD_TTS_MODEL_ID)
  }

  return VOICE_CLONING_ENGINE_OPTIONS.filter(option => {
    if (option.id === STANDARD_TTS_MODEL_ID) { return true }

    return option.id === ODUB_MODEL_ID
  })
}

export const getVoiceCloningEngineOptionsForLanguages = (languageCodes: (string | null | undefined)[]) => {
  if (!SHOW_ODUB_TTS_MODEL_OPTIONS) {
    return VOICE_CLONING_ENGINE_OPTIONS.filter(option => option.id === STANDARD_TTS_MODEL_ID)
  }

  const relevantLanguageCodes = languageCodes.filter((languageCode): languageCode is string => !!languageCode)

  if (relevantLanguageCodes.length === 0) {
    return VOICE_CLONING_ENGINE_OPTIONS
  }

  const automaticOdubVoiceCloningModelId = getAutomaticOdubVoiceCloningModelIdForLanguages(relevantLanguageCodes)

  return VOICE_CLONING_ENGINE_OPTIONS.filter(option => {
    if (option.id === STANDARD_TTS_MODEL_ID) { return true }

    return option.id === ODUB_MODEL_ID && !!automaticOdubVoiceCloningModelId
  })
}

export const getDefaultVoiceCloningEngineIdForLanguages = (languageCodes: (string | null | undefined)[]) => {
  const availableOptions = getVoiceCloningEngineOptionsForLanguages(languageCodes)

  if (availableOptions.some(option => option.id === DEFAULT_ODUB_VOICE_CLONING_MODEL_ID)) {
    return DEFAULT_ODUB_VOICE_CLONING_MODEL_ID
  }

  return STANDARD_TTS_MODEL_ID
}

export const getOdubModelLabel = (id: string | null | undefined) => {
  switch (id) {
  case ODUB_DESIGN_VOICE_CLONE_MODEL_ID:
    return ODUB_DESIGN_VOICE_CLONE_MODEL_LABEL
  case ODUB_MULTILINGUAL_MODEL_ID:
    return ODUB_MULTILINGUAL_MODEL_LABEL
  case ODUB_FINETUNED_MODEL_ID:
    return ODUB_FINETUNED_MODEL_LABEL
  case STANDARD_TTS_MODEL_ID:
  default:
    return STANDARD_TTS_MODEL_LABEL
  }
}

export const getVoiceCloningEngineLabel = (id: string) => {
  return VOICE_CLONING_ENGINE_OPTIONS.find(option => option.id === id)?.label || VOICE_CLONING_ENGINE_OPTIONS[0].label
}

export const getVoiceCloningEngineButtonLabel = (id: string) => {
  return VOICE_CLONING_ENGINE_OPTIONS.find(option => option.id === id)?.buttonLabel || VOICE_CLONING_ENGINE_OPTIONS[0].buttonLabel
}

export const getVoiceCloningEngineIdFromVoiceEngine = (voiceEngine: string | null | undefined) => {
  return voiceEngine === ODUB_MODEL_ID ? ODUB_MODEL_ID : STANDARD_TTS_MODEL_ID
}

export const getVoiceCloningEngineId = (label: string | null | undefined) => {
  if ([ODUB_MULTILINGUAL_MODEL_LABEL, ODUB_FINETUNED_MODEL_LABEL, ODUB_DESIGN_VOICE_CLONE_MODEL_LABEL].includes(label || '')) {
    return ODUB_MODEL_ID
  }

  return VOICE_CLONING_ENGINE_OPTIONS.find(option => option.label === label)?.id || STANDARD_TTS_MODEL_ID
}

export const isOdubVoiceCloningModelId = (value: string | null | undefined): value is typeof ODUB_MULTILINGUAL_MODEL_ID | typeof ODUB_FINETUNED_MODEL_ID | typeof ODUB_DESIGN_VOICE_CLONE_MODEL_ID => {
  return value === ODUB_MULTILINGUAL_MODEL_ID || value === ODUB_FINETUNED_MODEL_ID || value === ODUB_DESIGN_VOICE_CLONE_MODEL_ID
}
